import { useRouterState } from '@tanstack/react-router'

import { cn } from '@/shared/lib'

/**
 * The bar at the top edge while a navigation is in flight.
 *
 * Routes resolve their data before their page renders, which is what stops the
 * panel rearranging itself as content lands — but it means a click can leave
 * the previous screen up while the request runs, and with nothing said that
 * reads as a click that did not register. This says it.
 *
 * The familiar shape, and familiarity is the whole argument for it: the bar
 * runs out fast, decelerates, parks short of the end, and on completion covers
 * the rest and goes. Strictly it claims a progress nobody measured — 85% is
 * where the curve happens to be, not how much has loaded — and an honest
 * indeterminate sweep was the first version. It lost: almost everyone has read
 * this exact bar a thousand times, and nobody reads it as a percentage.
 *
 * **Every navigation shows it.** The version before this waited 400ms so a fast
 * one never saw it, which is defensible on paper — under a second a delay does
 * not interrupt thought — and wrong in the hand: whether the wait crosses 400ms
 * depends on whether the cache is warm, which nobody can see, so the bar
 * appeared at random. Feedback that fires sometimes is worse than feedback that
 * always fires.
 *
 * No timers and no state. `location.href` changes the moment a navigation
 * starts, well before it commits, so keying on it restarts the crawl from zero
 * exactly once per navigation; the end is a class swap that finishes the width
 * and fades. The fade's delay is also the bar's minimum life — an instant
 * navigation still leaves it on screen long enough to be read rather than
 * glimpsed.
 */
export function RouteProgress() {
  const isNavigating = useRouterState({ select: (state) => state.status === 'pending' })
  const href = useRouterState({ select: (state) => state.location.href })

  return (
    <div
      // `aria-hidden`: the state it reports is the page changing, which the
      // router announces by moving focus. A live region here would talk over
      // that with "loading" on every navigation.
      aria-hidden
      data-slot="route-progress"
      className="pointer-events-none fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden"
    >
      <div
        // Remounts when a navigation begins, which is what rewinds the crawl.
        key={href}
        className={cn(
          'h-full bg-primary shadow-primary-glow',
          isNavigating
            ? // 85% is the element's own width, and the keyframe only supplies
              // the `from`. Dropping the animation on completion therefore
              // leaves the bar where it stood instead of snapping back.
              'animate-progress w-[85%] opacity-100'
            : // Two properties on two schedules: the width covers the last of
              // the distance first, and only then does the bar fade. The
              // opacity's 150ms delay is also its minimum life — a navigation
              // that resolves instantly is still read rather than glimpsed.
              'w-full opacity-0 transition-[width,opacity] [transition-delay:0ms,150ms] [transition-duration:150ms,200ms]',
        )}
      />
    </div>
  )
}
